import {
    ResponseError,
    UserReportSource,
    type UserReportCategory,
    type UserReportCreate,
    type UserReportRead,
} from "@openapi";
import type { TFunction } from "i18next";
import api from "@/lib/api";

// User reports (blocks-code/management#118): a logged-in user sends a bug,
// content problem or suggestion from the studio. The backend allows 5 reports
// per hour and 20 per day per user and answers 429 with a readable detail.

// The backend column for the user agent is this long.
export const USER_AGENT_MAX_LENGTH = 512;

export interface UserReportFields {
    category: UserReportCategory;
    title: string;
    description: string;
}

/** Where the report was sent from: `window.location.href` and `navigator.userAgent`. */
export interface UserReportContext {
    href: string;
    userAgent: string;
}

export type SubmitUserReportResult =
    | { ok: true; report: UserReportRead }
    | {
          ok: false;
          message: string;
          /** Server validation messages by field name (422 only). */
          fieldErrors: Record<string, string>;
      };

export function buildUserReportCreate(
    { category, title, description }: UserReportFields,
    { href, userAgent }: UserReportContext,
): UserReportCreate {
    return {
        category,
        title,
        description,
        source: UserReportSource.Studio,
        page_url: href,
        user_agent: userAgent.slice(0, USER_AGENT_MAX_LENGTH),
    };
}

// Reads the FastAPI `detail` from a failed generated-client call.
async function errorDetail(error: ResponseError): Promise<unknown> {
    try {
        const body = (await error.response.clone().json()) as {
            detail?: unknown;
        };
        return body.detail;
    } catch {
        return undefined;
    }
}

interface ValidationItem {
    field?: string;
    msg: string;
}

// FastAPI validation errors come as a list of { loc, msg }; the last `loc`
// entry is the field name.
function validationItems(detail: unknown): ValidationItem[] {
    if (!Array.isArray(detail)) {
        return [];
    }
    return (detail as unknown[])
        .filter(
            (item): item is { loc?: unknown; msg: string } =>
                !!item &&
                typeof item === "object" &&
                typeof (item as { msg?: unknown }).msg === "string",
        )
        .map(({ loc, msg }) => {
            const last: unknown = Array.isArray(loc)
                ? loc[loc.length - 1]
                : undefined;
            const field =
                (typeof last === "string" || typeof last === "number") &&
                last !== "body"
                    ? String(last)
                    : undefined;
            return { field, msg };
        });
}

function validationDetails(detail: unknown): string | undefined {
    if (typeof detail === "string") {
        return detail;
    }
    const parts = validationItems(detail).map(({ field, msg }) =>
        field ? `${field}: ${msg}` : msg,
    );
    return parts.length > 0 ? parts.join("; ") : undefined;
}

/** The first validation message per field of a 422, keyed by field name. */
export async function userReportFieldErrors(
    error: unknown,
): Promise<Record<string, string>> {
    const fieldErrors: Record<string, string> = {};
    if (!(error instanceof ResponseError) || error.response.status !== 422) {
        return fieldErrors;
    }
    for (const { field, msg } of validationItems(await errorDetail(error))) {
        if (field && !(field in fieldErrors)) {
            fieldErrors[field] = msg;
        }
    }
    return fieldErrors;
}

// Returns a translated message for a failed report submission.
export async function userReportErrorMessage(
    error: unknown,
    t: TFunction,
): Promise<string> {
    if (!(error instanceof ResponseError)) {
        return t("message.unknownError");
    }
    const status = error.response.status;
    if (status === 401) {
        return t("userReports.error.loginRequired");
    }
    const detail = await errorDetail(error);
    if (status === 422) {
        const details = validationDetails(detail);
        if (details) {
            return t("userReports.error.validation", { details });
        }
    }
    if (status === 429) {
        return typeof detail === "string" && detail
            ? detail
            : t("userReports.error.rateLimited");
    }
    return t("userReports.error.generic", { status });
}

export async function submitUserReport(
    fields: UserReportFields,
    context: UserReportContext,
    t: TFunction,
): Promise<SubmitUserReportResult> {
    try {
        const report = await api.userReports.createUserReport({
            userReportCreate: buildUserReportCreate(fields, context),
        });
        return { ok: true, report };
    } catch (error) {
        return {
            ok: false,
            message: await userReportErrorMessage(error, t),
            fieldErrors: await userReportFieldErrors(error),
        };
    }
}
